import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import {
  type EditableCharacter,
  clearConsequence,
  setConsequence,
} from "../lib/character-edits.js";

interface SlotDef {
  id: string;
  value: number;
  labelKey: string;
}

const DEFAULT_SLOTS: SlotDef[] = [
  { id: "mild", value: 2, labelKey: "consequences.mild" },
  { id: "moderate", value: 4, labelKey: "consequences.moderate" },
  { id: "severe", value: 6, labelKey: "consequences.severe" },
];

interface Props {
  characterPath: string;
  character: EditableCharacter;
  slots?: SlotDef[];
}

export default function CharacterConsequencesEditor({
  characterPath,
  character,
  slots = DEFAULT_SLOTS,
}: Props): ReactElement {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  async function onSet(slotId: string, text: string): Promise<void> {
    const { db } = getFirebase();
    if (text.trim().length === 0) {
      await applyWriteMap(db, clearConsequence(characterPath, slotId));
    } else {
      await applyWriteMap(db, setConsequence(characterPath, slotId, { text: text.trim() }));
    }
    setDrafts((d) => ({ ...d, [slotId]: "" }));
  }

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase text-muted-foreground">
        {t("sheet.consequences")}
      </h2>
      <ul className="mt-2 space-y-2 text-sm">
        {slots.map((s) => {
          const filled = character.consequences?.[s.id];
          const draft = drafts[s.id] ?? filled?.aspect.text ?? "";
          return (
            <li key={s.id} className="flex items-center gap-2">
              <span className="w-28">
                {t(s.labelKey)} ({s.value})
              </span>
              <input
                className="flex-1 rounded border px-2 py-1"
                placeholder={t("edit.consequence.placeholder")}
                value={draft}
                onChange={(e) => setDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                aria-label={`${s.id} consequence text`}
              />
              <button
                type="button"
                className="rounded border px-2 py-1 text-xs"
                onClick={() => onSet(s.id, draft)}
              >
                {filled ? t("edit.save") : t("edit.consequence.take")}
              </button>
              {filled && (
                <button
                  type="button"
                  className="rounded border border-rose-400 px-2 py-1 text-xs text-rose-700"
                  onClick={() => onSet(s.id, "")}
                >
                  {t("edit.consequence.clear")}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
